import { useCallback, useEffect, useState } from "react";
import { Check, LogOut, RotateCcw, Trash2 } from "lucide-react";
import { Ornament } from "@/components/Ornament";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "roganel_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (t) => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/admin/guestbook`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
          return;
        }
        if (res.ok) setMessages(await res.json());
        const resContact = await fetch(`${API}/admin/contact`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        if (resContact.ok) setContacts(await resContact.json());
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Identifiants incorrects");
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch {
      setError("Erreur de connexion, merci de réessayer.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setMessages([]);
  };

  const setApproval = async (id, approved) => {
    await fetch(`${API}/admin/guestbook/${id}/approval`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ approved }),
    });
    load(token);
  };

  const remove = async (id) => {
    await fetch(`${API}/admin/guestbook/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    load(token);
  };

  const removeContact = async (id) => {
    await fetch(`${API}/admin/contact/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    load(token);
  };

  if (!token) {
    return (
      <div className="mx-auto max-w-md px-6 py-20 md:py-28" data-testid="admin-login-page">
        <div className="text-center">
          <p className="eyebrow">Roganel — Espace privé</p>
          <h1 className="mt-6 font-serif text-3xl font-medium tracking-[0.12em] text-noir" data-testid="admin-login-title">
            ADMINISTRATION
          </h1>
          <div className="mt-8">
            <Ornament />
          </div>
        </div>
        <form onSubmit={login} className="mt-12" data-testid="admin-login-form">
          <label htmlFor="admin-email" className="eyebrow block">Adresse e-mail</label>
          <input
            id="admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="admin-email-input"
            className="mt-3 w-full border border-bordeaux/30 bg-transparent px-4 py-3.5 font-sans text-sm font-light text-noir outline-none transition-colors duration-300 focus:border-bordeaux"
          />
          <label htmlFor="admin-password" className="eyebrow mt-8 block">Mot de passe</label>
          <input
            id="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="admin-password-input"
            className="mt-3 w-full border border-bordeaux/30 bg-transparent px-4 py-3.5 font-sans text-sm font-light text-noir outline-none transition-colors duration-300 focus:border-bordeaux"
          />
          {error && (
            <p className="mt-4 text-center font-sans text-xs text-bordeaux" data-testid="admin-login-error">{error}</p>
          )}
          <div className="mt-10 text-center">
            <button type="submit" className="btn-primary" data-testid="admin-login-button">
              Se connecter
            </button>
          </div>
        </form>
      </div>
    );
  }

  const pending = messages.filter((m) => !m.approved);
  const published = messages.filter((m) => m.approved);

  const renderMessage = (m, isPending) => (
    <div key={m.id} className="border border-bordeaux/20 p-6" data-testid={`admin-message-${m.id}`}>
      <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-bordeaux">{m.name}</p>
      <p className="mt-3 font-serif text-lg italic leading-relaxed text-noir/85">« {m.message} »</p>
      <div className="mt-6 flex flex-wrap gap-3">
        {isPending ? (
          <button
            type="button"
            onClick={() => setApproval(m.id, true)}
            data-testid={`admin-approve-${m.id}`}
            className="inline-flex items-center gap-2 bg-bordeaux px-5 py-2.5 font-sans text-[10px] uppercase tracking-[0.25em] text-ivory transition-colors duration-300 hover:bg-bordeaux-deep"
          >
            <Check size={13} strokeWidth={1.5} />
            Publier
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setApproval(m.id, false)}
            data-testid={`admin-unpublish-${m.id}`}
            className="inline-flex items-center gap-2 border border-bordeaux/40 px-5 py-2.5 font-sans text-[10px] uppercase tracking-[0.25em] text-bordeaux transition-colors duration-300 hover:bg-bordeaux hover:text-ivory"
          >
            <RotateCcw size={13} strokeWidth={1.5} />
            Retirer
          </button>
        )}
        <button
          type="button"
          onClick={() => remove(m.id)}
          data-testid={`admin-delete-${m.id}`}
          className="inline-flex items-center gap-2 border border-noir/20 px-5 py-2.5 font-sans text-[10px] uppercase tracking-[0.25em] text-noir/60 transition-colors duration-300 hover:border-bordeaux hover:text-bordeaux"
        >
          <Trash2 size={13} strokeWidth={1.5} />
          Supprimer
        </button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl px-6 py-16 md:px-8 md:py-24" data-testid="admin-dashboard">
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="eyebrow">Roganel — Espace privé</p>
          <h1 className="mt-5 font-serif text-3xl font-medium tracking-[0.1em] text-noir" data-testid="admin-dashboard-title">
            LIVRE D'OR
          </h1>
        </div>
        <button
          type="button"
          onClick={logout}
          data-testid="admin-logout-button"
          className="inline-flex items-center gap-2 border border-bordeaux/40 px-4 py-2.5 font-sans text-[10px] uppercase tracking-[0.25em] text-bordeaux transition-colors duration-300 hover:bg-bordeaux hover:text-ivory"
        >
          <LogOut size={13} strokeWidth={1.5} />
          Quitter
        </button>
      </div>
      <div className="mt-8">
        <Ornament />
      </div>

      {loading && <p className="mt-10 text-center font-sans text-xs text-noir/50">Chargement…</p>}

      <section className="mt-14" data-testid="admin-pending-section">
        <h2 className="font-serif text-2xl font-medium text-noir">
          En attente de validation ({pending.length})
        </h2>
        <div className="mt-6 space-y-6">
          {pending.length === 0 ? (
            <p className="font-sans text-sm font-light text-noir/50" data-testid="admin-pending-empty">
              Aucun message en attente.
            </p>
          ) : (
            pending.map((m) => renderMessage(m, true))
          )}
        </div>
      </section>

      <section className="mt-16" data-testid="admin-published-section">
        <h2 className="font-serif text-2xl font-medium text-noir">
          Messages publiés ({published.length})
        </h2>
        <div className="mt-6 space-y-6">
          {published.length === 0 ? (
            <p className="font-sans text-sm font-light text-noir/50" data-testid="admin-published-empty">
              Aucun message publié pour le moment.
            </p>
          ) : (
            published.map((m) => renderMessage(m, false))
          )}
        </div>
      </section>

      <section className="mt-16" data-testid="admin-contact-section">
        <h2 className="font-serif text-2xl font-medium text-noir">
          Messages reçus — La Boutique ({contacts.length})
        </h2>
        <div className="mt-6 space-y-6">
          {contacts.length === 0 ? (
            <p className="font-sans text-sm font-light text-noir/50" data-testid="admin-contact-empty">
              Aucun message reçu pour le moment.
            </p>
          ) : (
            contacts.map((c) => (
              <div key={c.id} className="border border-bordeaux/20 p-6" data-testid={`admin-contact-${c.id}`}>
                <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-bordeaux">{c.name}</p>
                <p className="mt-1 font-sans text-xs text-noir/50">{c.email}</p>
                <p className="mt-3 font-sans text-sm font-light leading-relaxed text-noir/85">{c.message}</p>
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => removeContact(c.id)}
                    data-testid={`admin-contact-delete-${c.id}`}
                    className="inline-flex items-center gap-2 border border-noir/20 px-5 py-2.5 font-sans text-[10px] uppercase tracking-[0.25em] text-noir/60 transition-colors duration-300 hover:border-bordeaux hover:text-bordeaux"
                  >
                    <Trash2 size={13} strokeWidth={1.5} />
                    Supprimer
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
